import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ShinyButton } from "@/components/ui/ShinyButton";
import { DiwaliOfferBadge, RangoliCorners } from "@/components/Festive";

const ROTATE_MS = 5000;
const PAIR_MQ = "(min-width: 768px)";

function usePerSlide() {
  const [perSlide, setPerSlide] = useState(
    () => typeof window !== "undefined" && window.matchMedia(PAIR_MQ).matches ? 2 : 1
  );
  useEffect(() => {
    const mq = window.matchMedia(PAIR_MQ);
    const onChange = (e) => setPerSlide(e.matches ? 2 : 1);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return perSlide;
}

function PromoCard({ card, index, paired, panelBase, pill, testidPrefix }) {
  const navigate = useNavigate();
  const festive = panelBase === "fest-hero-panel";
  return (
    <div
      role="link"
      tabIndex={0}
      data-testid={`${testidPrefix}-card-${index}`}
      onClick={() => navigate(card.link)}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(card.link); }}
      className={`${panelBase} ${panelBase}-${index % 2 === 0 ? "a" : "b"} relative min-h-[480px] cursor-pointer overflow-hidden rounded-[26px] p-6 sm:h-auto sm:min-h-[340px] sm:p-8 ${paired ? "w-[calc(50%-8px)] shrink-0" : "w-full shrink-0"}`}
    >
      {festive && <RangoliCorners />}
      {festive && <DiwaliOfferBadge className="absolute left-4 top-4 z-20" testid={`${testidPrefix}-offer-badge-${index}`} />}
      {/* two-column: text+CTA left (vertically centered), product card right; stacks on mobile */}
      <div className="pq-hero-content relative z-10 flex h-full flex-col sm:flex-row sm:items-center sm:gap-5 lg:gap-7">
        <div className="flex flex-1 flex-col justify-center">
          <span className="pq-hero-pill inline-block w-fit whitespace-nowrap rounded-full bg-white/20 px-3 py-1 text-[9px] font-extrabold uppercase tracking-[0.2em] text-white backdrop-blur-sm" data-testid={`${testidPrefix}-pill-${index}`}>
            {card.pill || pill}
          </span>
          <h1 className="font-display mt-3 text-[1.9rem] font-extrabold leading-[1.02] tracking-tight text-white sm:text-[2rem] lg:text-[3rem] xl:text-[3.2rem]" data-testid={`${testidPrefix}-headline-${index}`}>
            {card.headline}
          </h1>
          {card.subline && (
            <p className="pq-hero-subline mt-2 max-w-[38ch] text-xs font-medium leading-relaxed text-white/85 sm:text-sm">
              {card.subline}
            </p>
          )}
          <ShinyButton as="span" data-testid={`${testidPrefix}-cta-${index}`} className="mt-5 w-fit shadow-lg transition-transform duration-300 hover:scale-105" contentClassName="whitespace-nowrap px-5 py-2.5 text-xs font-extrabold sm:text-sm">
            Shop Now <ArrowRight className="h-4 w-4" />
          </ShinyButton>
        </div>
        {card.image && (
          <div className="mt-6 flex justify-center sm:mt-0 sm:shrink-0">
            <div className="flex h-[190px] w-[190px] items-center justify-center rounded-[22px] bg-white p-6 shadow-[0_18px_44px_-12px_rgba(0,0,0,0.45)] sm:h-[160px] sm:w-[160px] sm:p-5 lg:h-[216px] lg:w-[216px]">
              <img src={card.image} alt={card.subline || card.headline} className="max-h-full max-w-full object-contain" loading={index === 0 ? "eager" : "lazy"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * PromoCarousel — shared promo-card hero carousel (everyday + festival skins).
 * Two cards per slide on desktop/tablet, one on mobile. Auto-rotates (pauses
 * on hover/touch, off for reduced-motion), arrows, dots (per slide), swipe.
 */
export default function PromoCarousel({ cards, pill, panelBase, testidPrefix }) {
  const perSlide = usePerSlide();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const reducedMotion = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const slides = [];
  for (let i = 0; i < cards.length; i += perSlide) slides.push(cards.slice(i, i + perSlide));

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (reducedMotion.current || paused || slides.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  useEffect(() => { if (index >= slides.length) setIndex(0); }, [slides.length, index]);

  if (cards.length === 0) return null;

  return (
    <section
      className="relative mx-auto max-w-7xl px-4 pt-28 sm:px-6 lg:px-12"
      data-testid={`${testidPrefix}-carousel`}
      aria-roledescription="carousel"
      aria-label="Featured offers"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; setPaused(true); }}
      onTouchEnd={(e) => {
        setPaused(false);
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <div className="pq-carousel-viewport overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slideCards, s) => (
            <div key={s} className="flex w-full shrink-0 gap-4" data-testid={`${testidPrefix}-slide-${s}`}>
              {slideCards.map((card, i) => (
                <PromoCard
                  key={`${card.kind}-${s}-${i}`}
                  card={card}
                  index={s * perSlide + i}
                  paired={perSlide === 2}
                  panelBase={panelBase}
                  pill={pill}
                  testidPrefix={testidPrefix}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous offer"
            data-testid={`${testidPrefix}-prev`}
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="pq-carousel-arrow absolute left-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-neutral-800 shadow-lg transition-transform hover:scale-110 sm:left-3"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Next offer"
            data-testid={`${testidPrefix}-next`}
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="pq-carousel-arrow absolute right-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-neutral-800 shadow-lg transition-transform hover:scale-110 sm:right-3"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="mt-4 flex justify-center gap-2" data-testid={`${testidPrefix}-dots`}>
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to offer slide ${i + 1}`}
                data-testid={`${testidPrefix}-dot-${i}`}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                className={`pq-dot h-[11px] rounded-full transition-all duration-500 motion-reduce:transition-none ${i === index ? "pq-dot-active w-[38px] bg-[var(--pq-accent)]" : "w-[11px] bg-[#3a3a3a] hover:bg-[#555555]"}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
